import { TentTypeV1, TentTypeV2, getDateString } from '@camp67/model';
import { retry } from '../utils/retry';
import { environmentState } from '../state/environment';
import type { RoomsPerDay } from '../state/reservation-store';

/**
 * Query available tent types for a given date range.
 * Uses the shared environment state for API URL.
 */
export async function queryTentAvailability(
  startDate: string,
  endDate: string,
): Promise<(TentTypeV1 | TentTypeV2)[]> {
  const base = environmentState.apiUrl;
  if (!base) {
    throw new Error('API URL not initialized');
  }

  const url = new URL(base);
  url.pathname += 'api/reservation/availability';

  const response = await retry('query-availability', (signal) =>
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        startDate: getDateString(startDate),
        endDate: getDateString(endDate),
      }),
      credentials: 'include',
      signal,
    }),
  );

  if (!response.ok) {
    const error = new Error(response.statusText);
    throw error;
  }

  const tents = await response.json<(TentTypeV1 | TentTypeV2)[]>();
  return tents;
}

/**
 * Query available dates for selected rooms.
 * Uses the shared environment state for API URL.
 */
export async function queryTentAvailabilityDates(opts: {
  rooms: [TentTypeV1 | TentTypeV2, number][];
}): Promise<RoomsPerDay> {
  const base = environmentState.apiUrl;
  if (!base) {
    throw new Error('API URL not initialized');
  }

  const url = new URL(base);
  url.pathname += 'api/reservation/availability/dates';

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(opts),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = new Error(response.statusText);
    throw error;
  }

  const dates = await response.json<Record<string, string[]>>();

  return dates;
}
